import { Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const triggerClasses =
  'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm';

export type ComboboxOption = { value: string; label: string };

type Props = {
  options: ComboboxOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  /** Shown on the trigger and as the clear row while nothing is selected. */
  anyLabel: string;
  searchPlaceholder: string;
  emptyLabel: string;
  /** Warns that the shop has more options than were loaded. */
  truncatedLabel?: string;
  disabled?: boolean;
  ariaLabel: string;
};

/**
 * Searchable single-select for the collection and tag catalog filters.
 *
 * Both lists come from one Shopify call made when the page opens, so filtering is
 * local and instant. `productTags` has no server-side search argument at all, which
 * is why neither filter queries per keystroke - and why a shop past the fetched
 * page gets `truncatedLabel` rather than a silently short list.
 */
export function CatalogFilterCombobox({
  options,
  value,
  onChange,
  anyLabel,
  searchPlaceholder,
  emptyLabel,
  truncatedLabel,
  disabled = false,
  ariaLabel,
}: Props) {
  const [open, setOpen] = useState(false);

  const selected = options.find((option) => option.value === value);
  // A value can outlive its option when the shop's list is truncated; show the raw
  // value rather than silently falling back to "any".
  const triggerLabel = selected?.label ?? value ?? anyLabel;

  const select = (next: string | null) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(triggerClasses, disabled && 'cursor-not-allowed opacity-50')}
        >
          <span className={cn('truncate', value ? 'text-black' : 'text-muted-foreground')}>
            {triggerLabel}
          </span>
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[16rem] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              <CommandItem value={anyLabel} onSelect={() => select(null)}>
                <Check className={cn('mr-2 size-4', value ? 'opacity-0' : 'opacity-100')} />
                {anyLabel}
              </CommandItem>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => select(option.value)}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      value === option.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {truncatedLabel ? (
            <p className="border-t px-3 py-2 text-xs leading-4 text-muted-foreground">
              {truncatedLabel}
            </p>
          ) : null}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
